import type { CSSObject, Breakpoint } from '@mui/material/styles';
import type { TypographyOptions } from '@mui/material/styles/createTypography';

import { pxToRem, setFont } from 'minimal-shared/utils';

import { createTheme as getTheme } from '@mui/material/styles';

import { themeConfig } from '../theme-config';

// ----------------------------------------------------------------------

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../extend-theme-types.d.ts}
 */
export type FontStyleExtend = {
  fontWeightSemiBold: CSSObject['fontWeight'];
  fontSecondaryFamily: CSSObject['fontFamily'];
};

export type ResponsiveFontSizesInput = Partial<Record<Breakpoint, number>>;
export type ResponsiveFontSizesResult = Record<string, { fontSize: string }>;

const defaultMuiTheme = getTheme();

function responsiveFontSizes(obj: ResponsiveFontSizesInput): ResponsiveFontSizesResult {
  const breakpoints: Breakpoint[] = defaultMuiTheme.breakpoints.keys;

  return breakpoints.reduce((acc, breakpoint) => {
    const value = obj[breakpoint];

    if (value !== undefined && value >= 0) {
      acc[defaultMuiTheme.breakpoints.up(breakpoint)] = {
        fontSize: pxToRem(value),
      };
    }

    return acc;
  }, {} as ResponsiveFontSizesResult);
}

// ----------------------------------------------------------------------

const primaryFont = setFont(themeConfig.fontFamily.primary);
const secondaryFont = setFont(themeConfig.fontFamily.secondary);
const tertiaryFont = setFont(themeConfig.fontFamily.tertiary);
const quaternaryFont = setFont(themeConfig.fontFamily.quaternary);
const fifthFont = setFont(themeConfig.fontFamily.fifth);

export const typography: TypographyOptions = {
  fontFamily: primaryFont,
  fontSecondaryFamily: secondaryFont,
  fontWeightLight: '300',
  fontWeightRegular: '400',
  fontWeightMedium: '500',
  fontWeightSemiBold: '600',
  fontWeightBold: '700',
  h1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(20),
    ...responsiveFontSizes({ sm: 36, md: 60 }),
  },
  h2: {
    fontFamily: tertiaryFont,
    fontWeight: 400,
    lineHeight: 1.25,
    fontSize: pxToRem(14),
    ...responsiveFontSizes({ sm: 32, md: 48 }),
  },
  h3: {
    fontFamily: secondaryFont,
    fontWeight: 600,
    lineHeight: 1.15,
    fontSize: pxToRem(12),
    ...responsiveFontSizes({ sm: 24, md: 36 }),
  },
  h4: {
    fontFamily: tertiaryFont,
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: pxToRem(14),
    ...responsiveFontSizes({ sm: 20, md: 24 }),
  },
  h5: {
    fontWeight: 700,
    lineHeight: 1.15,
    fontSize: pxToRem(18),
    ...responsiveFontSizes({ sm: 22, md: 22 }),
  },
  h6: {
    fontWeight: 600,
    lineHeight: 1,
    fontSize: pxToRem(16),
    ...responsiveFontSizes({ sm: 16, md: 20 }),
  },
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  subtitle2: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  body1: {
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    ...responsiveFontSizes({ sm: 14, md: 16 }),
  },
  body2: {
    lineHeight: 1.25,
    fontSize: pxToRem(10),
    ...responsiveFontSizes({ sm: 12, md: 14 }),
  },
  caption: {
    lineHeight: 1.15,
    fontSize: pxToRem(10),
    ...responsiveFontSizes({ sm: 11, md: 13 }),
  },
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    textTransform: 'none',
  },
  button: {
    fontWeight: 700,
    lineHeight: 1.15,
    fontSize: pxToRem(12),
    ...responsiveFontSizes({ sm: 16, md: 16 }),
    textTransform: 'unset',
  },
};
